import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useId, useMemo } from 'react';
import classNames from 'classnames';
import { XIcon } from '@repo/icons';
import { getLabels } from '../labels';
const containerBaseClasses = 'relative inline-flex gap-2 border overflow-hidden';
const sizeClasses = {
    sm: 'px-2.5 py-2 text-sm rounded-lg',
    base: 'px-3 py-2.5 text-sm rounded-xl',
    lg: 'px-3.5 py-3 text-base rounded-xl',
    xl: 'px-4 py-3.5 text-base rounded-xl',
};
const stateClasses = {
    default: 'border-stroke-base-medium text-body-subtle focus-within:bg-secondary-medium focus-within:border-brand focus-within:text-fg-brand',
    success: 'text-fg-success-strong border-stroke-success-subtle bg-success-soft',
    danger: 'text-fg-danger-strong border-stroke-danger-subtle bg-danger-soft',
};
const disabledClasses = 'bg-secondary-medium border-stroke-base-medium text-fg-disabled cursor-not-allowed opacity-50';
const readonlyClasses = 'bg-tertiary border-stroke-base-medium text-body';
const flatClasses = '';
const nonFlatClasses = 'shadow-sm bg-secondary-medium';
const inputClasses = 'w-full bg-transparent border-none outline-none placeholder:text-body-subtle text-inherit focus:text-heading';
const Input = forwardRef(function Input(props, ref) {
    const { testId, label, size = 'base', state = 'default', disabled = false, readOnly = false, clearable = false, flat = false, leftIcon: LeftIcon, rightIcon: RightIcon, leftElement, rightElement, value, className, onChange, onClear, onRightIconClick, onTriggerClick, multiline, ...rest } = props;
    const inputId = useId();
    const { clearAriaLabel } = getLabels().input;
    const showClearButton = useMemo(() => {
        if (!clearable || disabled)
            return false;
        if (value === undefined || value === null)
            return false;
        return String(value).length > 0;
    }, [clearable, disabled, value]);
    const handleClear = () => {
        if (disabled)
            return;
        onChange?.('');
        onClear?.();
    };
    const handleContainerClick = (event) => {
        if (!onTriggerClick || disabled)
            return;
        if (event.target.closest('button') !== null)
            return;
        onTriggerClick();
    };
    const handleInputKeyDown = (event) => {
        if (!onTriggerClick || disabled)
            return;
        if (event.key !== ' ' && event.key !== 'Enter')
            return;
        event.preventDefault();
        onTriggerClick();
    };
    const containerClasses = classNames(containerBaseClasses, multiline ? 'items-start' : 'items-center', sizeClasses[size], disabled ? disabledClasses : readOnly ? readonlyClasses : stateClasses[state], !disabled && 'focus-within:ring-2 focus-within:ring-brand', flat ? flatClasses : nonFlatClasses, onTriggerClick && !disabled && 'cursor-pointer', className);
    let inputElement;
    if (multiline) {
        const { ...textareaRest } = rest;
        inputElement = (_jsx("textarea", { id: inputId, className: classNames(inputClasses, 'resize-y'), disabled: disabled, readOnly: readOnly, value: value ?? '', onChange: (event) => onChange?.(event.target.value), onKeyDown: handleInputKeyDown, ref: ref, "data-testid": testId, ...textareaRest }));
    }
    else {
        const { type = 'text', ...inputRest } = rest;
        inputElement = (_jsx("input", { id: inputId, className: inputClasses, disabled: disabled, readOnly: readOnly, type: type, value: value ?? '', onChange: (event) => onChange?.(event.target.value), onKeyDown: handleInputKeyDown, ref: ref, "data-testid": testId, ...inputRest }));
    }
    const leftContent = leftElement ?? (LeftIcon ? _jsx(LeftIcon, { className: "size-4" }) : null);
    const rightIconElement = RightIcon ? _jsx(RightIcon, { className: "size-4" }) : null;
    const rightContent = rightElement ??
        (rightIconElement && onRightIconClick ? (_jsx("button", { type: "button", className: "cursor-pointer border-none bg-transparent p-0", onClick: onRightIconClick, children: rightIconElement })) : (rightIconElement));
    return (_jsxs("div", { className: "flex flex-col", children: [label && (_jsx("label", { htmlFor: inputId, className: "text-heading mb-2.5 text-sm leading-5 font-medium", "data-testid": testId?.append('label'), children: label })), _jsxs("div", { className: containerClasses, onClick: handleContainerClick, children: [leftContent && _jsx("div", { className: "flex shrink-0 items-center justify-center", children: leftContent }), inputElement, (showClearButton || rightContent) && (_jsxs("div", { className: "flex shrink-0 items-center gap-3", children: [showClearButton && (_jsx("button", { type: "button", "aria-label": clearAriaLabel, className: "cursor-pointer border-none bg-transparent p-0", onClick: handleClear, "data-testid": testId?.append('clear'), children: _jsx(XIcon, { size: 20 }) })), rightContent] }))] })] }));
});
export default Input;
